import { NestingCardsViewState } from "./state/nesting-cards-view-state";
import { NestingCardsPopupWorkflow } from "./workflow/nesting-cards-popup.workflow";
import { NestingCardsSaveWorkflow } from "./workflow/nesting-cards-save.workflow";
import { NestingCardsFiltersWorkflow } from "./workflow/nesting-cards-filters.workflow";
import { NestingCardsStatsWorkflow } from "./workflow/nesting-cards-stats.workflow";
import type { NestingCardsWorkflowDependencies } from "./workflow/nesting-cards-workflow-dependencies";
import { CdkDragDrop } from "@angular/cdk/drag-drop";
import {
  inject,
  Component,
  Input,
  OnInit,
  OnChanges,
  HostListener,
  Output,
  EventEmitter,
  ViewChild,
  ChangeDetectorRef,
} from "@angular/core";
import {
  CardConfig,
  getAllCardConfigs,
  NestGroupConfig,
  NestGroups,
  RowData,
} from "./nesting-summary-cards";
import { OverlayPanel } from "primeng/overlaypanel";

@Component({
  selector: "app-nesting-summary-cards",
  templateUrl: "./nesting-summary-cards.component.html",
  styleUrls: ["./nesting-summary-cards.component.scss"],
  standalone: false,
})
export class NestingSummaryCardsComponent
  extends NestingCardsViewState
  implements OnInit, OnChanges
{
  private readonly nestingCardsPopupWorkflow = inject(
    NestingCardsPopupWorkflow,
  );
  private readonly nestingCardsSaveWorkflow = inject(NestingCardsSaveWorkflow);
  private readonly nestingCardsFiltersWorkflow = inject(
    NestingCardsFiltersWorkflow,
  );
  private readonly nestingCardsStatsWorkflow = inject(
    NestingCardsStatsWorkflow,
  );
  private get workflowDependencies(): NestingCardsWorkflowDependencies {
    return {
      setDefaultNestGroup: (
        ...arguments_: Parameters<
          NestingSummaryCardsComponent["setDefaultNestGroup"]
        >
      ) => this.setDefaultNestGroup(...arguments_),
      applySavedSelectedRows: (
        ...arguments_: Parameters<
          NestingSummaryCardsComponent["applySavedSelectedRows"]
        >
      ) => this.applySavedSelectedRows(...arguments_),
      collectAllOriginalNestNumbers: (
        ...arguments_: Parameters<
          NestingSummaryCardsComponent["collectAllOriginalNestNumbers"]
        >
      ) => this.collectAllOriginalNestNumbers(...arguments_),
      buildUpdatedTempConfigs: (
        ...arguments_: Parameters<
          NestingSummaryCardsComponent["buildUpdatedTempConfigs"]
        >
      ) => this.buildUpdatedTempConfigs(...arguments_),
      recalculateTempConfigsStats: (
        ...arguments_: Parameters<
          NestingSummaryCardsComponent["recalculateTempConfigsStats"]
        >
      ) => this.recalculateTempConfigsStats(...arguments_),
      saveTempChanges: (
        ...arguments_: Parameters<
          NestingSummaryCardsComponent["saveTempChanges"]
        >
      ) => this.saveTempChanges(...arguments_),
      applyNewNestsFromTemp: (
        ...arguments_: Parameters<
          NestingSummaryCardsComponent["applyNewNestsFromTemp"]
        >
      ) => this.applyNewNestsFromTemp(...arguments_),
      applyTempRowsToExistingCards: (
        ...arguments_: Parameters<
          NestingSummaryCardsComponent["applyTempRowsToExistingCards"]
        >
      ) => this.applyTempRowsToExistingCards(...arguments_),
      findFirstClusterInfo: (
        ...arguments_: Parameters<
          NestingSummaryCardsComponent["findFirstClusterInfo"]
        >
      ) => this.findFirstClusterInfo(...arguments_),
      buildSkuUpdateFromTemp: (
        ...arguments_: Parameters<
          NestingSummaryCardsComponent["buildSkuUpdateFromTemp"]
        >
      ) => this.buildSkuUpdateFromTemp(...arguments_),
      findTargetCardForRows: (
        ...arguments_: Parameters<
          NestingSummaryCardsComponent["findTargetCardForRows"]
        >
      ) => this.findTargetCardForRows(...arguments_),
      setPlaceholderStats: (
        ...arguments_: Parameters<
          NestingSummaryCardsComponent["setPlaceholderStats"]
        >
      ) => this.setPlaceholderStats(...arguments_),
      collectMetrics: (
        ...arguments_: Parameters<
          NestingSummaryCardsComponent["collectMetrics"]
        >
      ) => this.collectMetrics(...arguments_),
      formatPplRange: (
        ...arguments_: Parameters<
          NestingSummaryCardsComponent["formatPplRange"]
        >
      ) => this.formatPplRange(...arguments_),
      getAllRowsFromConfigs: (
        ...arguments_: Parameters<
          NestingSummaryCardsComponent["getAllRowsFromConfigs"]
        >
      ) => this.getAllRowsFromConfigs(...arguments_),
      rowMatchesFilters: (
        ...arguments_: Parameters<
          NestingSummaryCardsComponent["rowMatchesFilters"]
        >
      ) => this.rowMatchesFilters(...arguments_),
      cardMatchesSkuCountFilter: (
        ...arguments_: Parameters<
          NestingSummaryCardsComponent["cardMatchesSkuCountFilter"]
        >
      ) => this.cardMatchesSkuCountFilter(...arguments_),
    };
  }
  @Input() nestGroupsList: NestGroups[] = [];
  private _nestGroupConfigs: NestGroupConfig[] = [];
  @Input()
  set nestGroupConfigs(value: NestGroupConfig[]) {
    const isInitialSet =
      !this._nestGroupConfigs || this._nestGroupConfigs.length === 0;
    this._nestGroupConfigs = value;
    if (isInitialSet) {
      return;
    }
    this.setDefaultNestGroup();
    this.setFilterData();
    const hasAppliedFilters = Object.keys(this.appliedFiltersState).some(
      (key) =>
        this.appliedFiltersState[key] &&
        this.appliedFiltersState[key].length > 0,
    );
    if (hasAppliedFilters) {
      this.reapplyFiltersAfterDataRefresh();
    } else {
      this.totalCardConfigs = getAllCardConfigs(this._nestGroupConfigs);
      this.totalItems = this.totalCardConfigs.length;
    }
    if (this.showMoveSkuPopup && this.selectedCardConfig) {
      setTimeout(() => {
        this.refreshPopupData();
        this.cd.detectChanges();
      }, 0);
    } else {
      this.cd.detectChanges();
    }
  }
  get nestGroupConfigs(): NestGroupConfig[] {
    return this._nestGroupConfigs;
  }
  @Input() showLoader = false;
  @Input() lockAssortment = false;
  @Input() accessType: "read" | "write" = "write";
  @Input() downloadLoader = false;
  @Input() total_nests: number = 0;
  @Output() nestGroupConfigsChange = new EventEmitter<CardConfig[]>();
  @Output() modifyAction = new EventEmitter<void>();
  @Output() downloadAction = new EventEmitter<any>();

  @ViewChild("filterPanel") filterPanel!: OverlayPanel;

  constructor(private cd: ChangeDetectorRef) {
    super();
  }

  ngOnInit() {
    this.updateCols();
    this.setDefaultNestGroup();
    this.setFilterData();
    this.totalCardConfigs = getAllCardConfigs(this.nestGroupConfigs);
    this.totalItems = this.totalCardConfigs.length;
    this.currentPage = 1;
  }

  ngOnChanges(changes: import("@angular/core").SimpleChanges): void {
    if (
      changes["nestGroupConfigs"] &&
      !changes["nestGroupConfigs"].firstChange
    ) {
      this.setDefaultNestGroup();
      this.setFilterData();
      const hasAppliedFilters = Object.keys(this.appliedFiltersState).some(
        (key) =>
          this.appliedFiltersState[key] &&
          this.appliedFiltersState[key].length > 0,
      );
      if (hasAppliedFilters) {
        this.reapplyFiltersAfterDataRefresh();
      } else {
        this.totalCardConfigs = getAllCardConfigs(this.nestGroupConfigs);
        this.totalItems = this.totalCardConfigs.length;
      }
      if (this.showMoveSkuPopup && this.selectedCardConfig) {
        this.refreshPopupData();
      }
      this.cd.detectChanges();
    }
  }

  @HostListener("window:resize")
  onResize() {
    this.updateCols();
  }

  private updateCols() {
    const w = window.innerWidth;
    if (w < 600) this.cols = 1;
    else this.cols = 3;
  }

  private setDefaultNestGroup(): void {
    const firstGroup = this.nestGroupsList[0];
    if (firstGroup) this.toggleNestGroup(firstGroup);
  }

  toggleNestGroup(group: NestGroups): void {
    return this.nestingCardsFiltersWorkflow.toggleNestGroup(
      this,
      this.workflowDependencies,
      group,
    );
  }

  toggleNestGroupInPopup(group: NestGroups): void {
    return this.nestingCardsPopupWorkflow.toggleNestGroupInPopup(
      this,
      this.workflowDependencies,
      group,
    );
  }

  private applySavedSelectedRows(selectedNestNumber: number): void {
    return this.nestingCardsPopupWorkflow.applySavedSelectedRows(
      this,
      this.workflowDependencies,
      selectedNestNumber,
    );
  }

  private collectAllOriginalNestNumbers(): Set<number> {
    return this.nestingCardsPopupWorkflow.collectAllOriginalNestNumbers(
      this,
      this.workflowDependencies,
    );
  }
  private buildUpdatedTempConfigs(
    match: NestGroupConfig,
    selectedNestNumber: number,
    groupValue: string,
    allOriginalNestNumbers: Set<number>,
  ): CardConfig[] {
    return this.nestingCardsPopupWorkflow.buildUpdatedTempConfigs(
      this,
      this.workflowDependencies,
      match,
      selectedNestNumber,
      groupValue,
      allOriginalNestNumbers,
    );
  }

  private recalculateTempConfigsStats(): void {
    return this.nestingCardsPopupWorkflow.recalculateTempConfigsStats(
      this,
      this.workflowDependencies,
    );
  }

  private saveTempChanges(): void {
    return this.nestingCardsPopupWorkflow.saveTempChanges(
      this,
      this.workflowDependencies,
    );
  }

  moveSku(config: CardConfig): void {
    return this.nestingCardsPopupWorkflow.moveSku(
      this,
      this.workflowDependencies,
      config,
    );
  }

  get cardDropListIds(): string[] {
    return this.tempCardConfigs.map((_, i) => "cardDropList" + i);
  }

  onDrop(event: CdkDragDrop<RowData[]>, _target: "selected" | number): void {
    return this.nestingCardsPopupWorkflow.onDrop(
      this,
      this.workflowDependencies,
      event,
      _target,
    );
  }

  onPopUpSave(): void {
    return this.nestingCardsSaveWorkflow.onPopUpSave(
      this,
      this.workflowDependencies,
    );
  }

  private applyNewNestsFromTemp(): void {
    return this.nestingCardsSaveWorkflow.applyNewNestsFromTemp(
      this,
      this.workflowDependencies,
    );
  }

  private applyTempRowsToExistingCards(): void {
    return this.nestingCardsSaveWorkflow.applyTempRowsToExistingCards(
      this,
      this.workflowDependencies,
    );
  }

  private findFirstClusterInfo(): {
    cluster_level: string;
    cluster_name: string;
  } {
    return this.nestingCardsSaveWorkflow.findFirstClusterInfo(
      this,
      this.workflowDependencies,
    );
  }

  private buildSkuUpdateFromTemp(): Record<
    string,
    { is_new_nest: boolean; skus: string[] }
  > {
    return this.nestingCardsSaveWorkflow.buildSkuUpdateFromTemp(
      this,
      this.workflowDependencies,
    );
  }

  onMoveSkuDialogClose(): void {
    return this.nestingCardsSaveWorkflow.onMoveSkuDialogClose(
      this,
      this.workflowDependencies,
    );
  }

  hasUnsavedChanges(): boolean {
    return this.nestingCardsSaveWorkflow.hasUnsavedChanges(
      this,
      this.workflowDependencies,
    );
  }

  closeDialog(): void {
    return this.nestingCardsSaveWorkflow.closeDialog(
      this,
      this.workflowDependencies,
    );
  }

  onDragStarted(): void {
    this.isDragging = true;
  }

  onDragEnded(): void {
    this.isDragging = false;
  }

  recalculateCardStats(rows: RowData[]): void {
    return this.nestingCardsStatsWorkflow.recalculateCardStats(
      this,
      this.workflowDependencies,
      rows,
    );
  }

  private findTargetCardForRows(rows: RowData[]): CardConfig | null {
    return this.nestingCardsStatsWorkflow.findTargetCardForRows(
      this,
      this.workflowDependencies,
      rows,
    );
  }

  private setPlaceholderStats(targetCard: CardConfig | null): void {
    return this.nestingCardsStatsWorkflow.setPlaceholderStats(
      this,
      this.workflowDependencies,
      targetCard,
    );
  }
  private collectMetrics(rows: RowData[]): {
    beerTypes: Set<string>;
    segments: Set<string>;
    brands: Set<string>;
    packTypes: Set<string>;
    ppls: number[];
    packSizes: Set<number>;
  } {
    return this.nestingCardsStatsWorkflow.collectMetrics(
      this,
      this.workflowDependencies,
      rows,
    );
  }

  private formatPplRange(ppls: number[]): string {
    return this.nestingCardsStatsWorkflow.formatPplRange(
      this,
      this.workflowDependencies,
      ppls,
    );
  }

  addNewNest(): void {
    return this.nestingCardsPopupWorkflow.addNewNest(
      this,
      this.workflowDependencies,
    );
  }

  isPlaceholderStats(
    stats: { label: string; value: string | number }[],
  ): boolean {
    return stats.every((s) => s.value === "-");
  }

  private setFilterData(): void {
    return this.nestingCardsFiltersWorkflow.setFilterData(
      this,
      this.workflowDependencies,
    );
  }

  private getAllRowsFromConfigs(): RowData[] {
    return this.nestingCardsFiltersWorkflow.getAllRowsFromConfigs(
      this,
      this.workflowDependencies,
    );
  }

  filteredItems(key: keyof typeof this.filters): (string | number)[] {
    return this.nestingCardsFiltersWorkflow.filteredItems(
      this,
      this.workflowDependencies,
      key,
    );
  }

  toggleSelection(key: keyof typeof this.filters, item: string | number): void {
    return this.nestingCardsFiltersWorkflow.toggleSelection(
      this,
      this.workflowDependencies,
      key,
      item,
    );
  }

  private rowMatchesFilters(row: RowData | any): boolean {
    return this.nestingCardsFiltersWorkflow.rowMatchesFilters(
      this,
      this.workflowDependencies,
      row,
    );
  }

  onFilterApply(): void {
    return this.nestingCardsFiltersWorkflow.onFilterApply(
      this,
      this.workflowDependencies,
    );
  }

  clearFilter(): void {
    return this.nestingCardsFiltersWorkflow.clearFilter(
      this,
      this.workflowDependencies,
    );
  }

  onModifyClick(): void {
    this.modifyAction.emit();
  }

  onDownloadClick(event: any): void {
    this.downloadAction.emit(event);
  }
  openFilterPanel(event: Event) {
    this.showAccordion = false;
    this.cd.detectChanges();
    setTimeout(() => {
      this.activeAccordionIndex = [];
      this.showAccordion = true;
      this.cd.detectChanges();
      try {
        this.filterPanel?.show(event);
      } catch {}
      setTimeout(() => {
        this.activeAccordionIndex = [];
        this.cd.detectChanges();
      }, 20);
    }, 0);
  }

  onFilterPanelShow() {
    this.activeAccordionIndex = [];
    this.cd.detectChanges();
  }

  onFilterPanelHide() {
    this.showAccordion = false;
    this.activeAccordionIndex = [];
    this.cd.detectChanges();
  }

  get totalPages() {
    return Math.ceil(this.totalItems / this.pageSize);
  }

  get startIndex() {
    return (this.currentPage - 1) * this.pageSize;
  }

  get endIndex() {
    return Math.min(this.startIndex + this.pageSize, this.totalItems);
  }

  get paginatedCards(): CardConfig[] {
    if (this.totalItems === 0) {
      return [];
    }
    if (this.currentPage < 1) {
      this.currentPage = 1;
    } else if (this.currentPage > this.totalPages) {
      this.currentPage = this.totalPages;
    }
    return this.totalCardConfigs.slice(this.startIndex, this.endIndex);
  }

  onPageSizeChange(event: any) {
    this.pageSize = Number(event.target.value);
    this.currentPage = 1;
    this.cd.detectChanges();
  }

  prevPage() {
    if (this.currentPage > 1) {
      this.currentPage--;
      this.cd.detectChanges();
    }
  }

  nextPage() {
    if (this.currentPage < this.totalPages) {
      this.currentPage++;
      this.cd.detectChanges();
    }
  }

  onPageInputChange(event: Event) {
    const input = (event.target as HTMLInputElement).value;
    let page = Number(input);
    if (isNaN(page) || page < 1) {
      page = 1;
    } else if (page > this.totalPages) {
      page = this.totalPages;
    }
    this.currentPage = page;
    this.cd.detectChanges();
  }

  private cardMatchesSkuCountFilter(card: CardConfig): boolean {
    return this.nestingCardsFiltersWorkflow.cardMatchesSkuCountFilter(
      this,
      this.workflowDependencies,
      card,
    );
  }

  get appliedFilters(): { label: string; value: string; key: string }[] {
    const applied: { label: string; value: string; key: string }[] = [];
    const seen = new Set<string>();
    for (const config of this.filterConfigs) {
      const selected = this.appliedFiltersState[config.key] || [];
      if (selected.length > 0) {
        selected.forEach((value) => {
          const stringValue = String(value).trim();
          const filterId = `${config.key}:${stringValue}`;
          if (!stringValue || seen.has(filterId)) return;
          seen.add(filterId);
          applied.push({
            label: config.label,
            value: stringValue,
            key: config.key,
          });
        });
      }
    }
    return applied;
  }

  removeFilter(key: string, value: string): void {
    return this.nestingCardsFiltersWorkflow.removeFilter(
      this,
      this.workflowDependencies,
      key,
      value,
    );
  }

  private reapplyFiltersAfterDataRefresh(): void {
    return this.nestingCardsFiltersWorkflow.reapplyFiltersAfterDataRefresh(
      this,
      this.workflowDependencies,
    );
  }

  private refreshPopupData(): void {
    return this.nestingCardsPopupWorkflow.refreshPopupData(
      this,
      this.workflowDependencies,
    );
  }
}
